import Layout from '@/components/Layout/Vanilla';
import Button from '@/components/Button';
import Hero from '@/components/Hero';
import IndustryList from '@/components/IndustryList';
import ContentBlock from '@/components/ContentBlock';
import IconContentBlock from '@/components/IconContentBlock';
import Section from '@/components/Section';
import SectionHero from '@/components/SectionHero';
import Heading from '@/components/renderers/Heading';
import Code from '@/components/renderers/Code';
import Paragraph from '@/components/renderers/Paragraph';
import Link from '@/components/renderers/Link';
import { fetchPage, gql } from '@/lib/graphcms';

const simpleQuery = `query {
  pages {
    id
    title
    slug
  }
}`;

const relationalQuery = `query {
  pages {
    id
    title
    slug
    authors(
      orderBy: createdAt__DESC,
    ) {
      id
      name
    }
  }
}`;

const filteredQuery = `query {
  pages(
    first: 5,
    locale: [de, en],
    stage: PUBLISHED,
    createdAt__DESC,
  ) {
    id
    title
    slug
    authors {
      id
      name
    }
  }
}`;

export default function QueryApi({ page, preview }) {
  const { contentBlocks, usps } = page;

  return (
    <Layout seo={page} preview={preview}>
      <Section>
        <Hero {...page}>
          <Button href="/contact" appearance="outline" colour="pink">
            Try GraphCMS for free &#8594;
          </Button>
        </Hero>
      </Section>
      <Section colour="light" size="small">
        <IndustryList entries={contentBlocks} Component={ContentBlock} />
      </Section>
      <Section size="small">
        <SectionHero
          noHr
          title="Level up your GraphQL queries"
          description="Combine one or more query arguments to filter, sort and translate your query response."
        />
        <div className="-mx-6 md:flex md:flex-wrap">
          {usps.map(IconContentBlock)}
        </div>
      </Section>
      <Section size="small" container="narow">
        <SectionHero
          title="GraphQL queries at a glance"
          description="Query GraphCMS via GraphQL for your content."
        />
        <article className="-mx-6 flex flex-col items-center lg:flex-row">
          <div className="mb-6 flex w-full items-center justify-center px-6 lg:mb-0 lg:w-1/2">
            <Code
              language="graphql"
              className="my-6 h-full w-full shadow-2xl"
              noCopy
            >
              {simpleQuery}
            </Code>
          </div>
          <div className="w-full px-6 lg:w-1/2">
            <div className="-mt-6">
              <Heading level={4}>Simple GraphQL Query</Heading>
              <Paragraph>
                Single Queries are just that! Query one object or content node.
              </Paragraph>
            </div>
          </div>
        </article>
        <article className="-mx-6 flex flex-col items-center lg:flex-row">
          <div className="mb-6 flex w-full items-center justify-center px-6 lg:mb-0 lg:w-1/2">
            <Code
              language="graphql"
              className="my-6 h-full w-full shadow-2xl"
              noCopy
            >
              {relationalQuery}
            </Code>
          </div>
          <div className="w-full px-6 lg:w-1/2">
            <div className="-mt-6">
              <Heading level={4}>Relational GraphQL Query</Heading>
              <Paragraph>
                Query for data across relations with GraphQL. GraphCMS takes
                care of the heavy lifting, providing you a single, declarative
                API to query your related content.
              </Paragraph>
            </div>
          </div>
        </article>
        <article className="-mx-6 flex flex-col items-center lg:flex-row">
          <div className="mb-6 flex w-full items-center justify-center px-6 lg:mb-0 lg:w-1/2">
            <Code
              language="graphql"
              className="my-6 h-full w-full shadow-2xl"
              noCopy
            >
              {filteredQuery}
            </Code>
          </div>
          <div className="w-full px-6 lg:w-1/2">
            <div className="-mt-6">
              <Heading level={4}>GraphQL Query with arguments</Heading>
              <Paragraph>
                Easily query for the <code>first</code> 5 articles in a
                different{' '}
                <Link href="/content-localization">
                  <code>locale</code>
                </Link>
                , and <code>stage</code>.
              </Paragraph>
            </div>
          </div>
        </article>
      </Section>
    </Layout>
  );
}

export const getStaticProps = async ({ preview = false }) => {
  const { page } =
    (await fetchPage(
      gql`
        query aboutUsPageQuery {
          page(where: { slug: "query-api" }) {
            ...pageData # Fragment is inside the method
          }
        }
      `,
      {
        preview,
      },
    )) || [];

  return {
    props: {
      preview,
      page,
    },
    revalidate: 3600,
  };
};
